<template>
  <v-card class="mx-auto debt-report" :loading="loading" :disabled="loading">
    <v-card-title>
      <v-icon left color="deep-orange darken-2">mdi-account-cash-outline</v-icon>
      รายชื่อนักศึกษาค้างชำระค่าธรรมเนียมการศึกษา
    </v-card-title>
    <v-card-subtitle>
      {{ selectedEducationLevelName() }} · ค่าธรรมเนียมจากรายการลงทะเบียน
      หักยอดชำระจาก Billing และรายการชำระสำเร็จที่รอนำเข้า Billing
    </v-card-subtitle>

    <v-card-text>
      <v-form ref="form" v-model="formValid" @submit.prevent="search">
        <v-row dense>
          <v-col cols="12" sm="6" md="3">
            <v-select v-model="filters.levelId" :items="educationLevels" item-value="value"
              item-text="text" label="ระดับการศึกษา" outlined dense :rules="requiredRules"
              @change="onLevelChanged" />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select v-model="filters.year" :items="years" label="ปีการศึกษา"
              outlined dense :rules="requiredRules" />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select v-model="filters.semester" :items="semesters" label="ภาคการศึกษา"
              outlined dense :rules="requiredRules" />
          </v-col>
          <v-col cols="12" md="5">
            <v-autocomplete v-model="filters.facultyId" :items="faculties"
              :item-value="facultyId" :item-text="facultyName" label="คณะ"
              outlined dense clearable :rules="requiredRules" @change="onFacultyChanged" />
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete v-model="filters.departmentId" :items="filteredDepartments"
              :item-value="departmentId" :item-text="departmentName" label="ภาควิชา (ทั้งหมด)"
              outlined dense clearable :disabled="!filters.facultyId"
              @change="filters.curriculumId = null" />
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete v-model="filters.curriculumId" :items="filteredCurriculums"
              :item-value="curriculumId" :item-text="curriculumName" label="สาขาวิชา (ทั้งหมด)"
              outlined dense clearable :disabled="!filters.departmentId" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="filters.fundMode" :items="fundModes" item-value="value"
              item-text="text" label="การแสดงทุน" outlined dense />
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mb-4 report-actions">
          <v-btn color="deep-orange darken-2" dark type="submit" :loading="loading">
            <v-icon left>mdi-magnify</v-icon>แสดงข้อมูล
          </v-btn>
          <v-btn class="ml-2" outlined color="green darken-2" :disabled="!items.length" @click="downloadExcel">
            <v-icon left>mdi-microsoft-excel</v-icon>ดาวน์โหลด Excel
          </v-btn>
        </div>
      </v-form>

      <template v-if="hasSearched">
        <v-alert v-if="paymentConfig" dense text type="info">
          ค่าปรับวันละ {{ formatMoney(paymentConfig.PAY_LATE_CHARGE_PER_DAY) }} บาท
          ตั้งแต่ {{ formatDate(paymentConfig.START_DATE) }} ถึง {{ formatDate(paymentConfig.END_DATE) }}
        </v-alert>

        <v-row dense class="mb-3">
          <v-col cols="12" sm="6">
            <v-card outlined class="pa-4 summary-card">
              <div class="text-caption grey--text">นักศึกษาค้างชำระ</div>
              <div class="text-h5 font-weight-bold">{{ Number(summary.student_count).toLocaleString() }} คน</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card outlined class="pa-4 summary-card">
              <div class="text-caption grey--text">ยอดค้างชำระรวม</div>
              <div class="text-h5 font-weight-bold deep-orange--text text--darken-3">
                {{ formatMoney(summary.debt_amount) }} บาท
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-alert v-if="!items.length && !loading" type="success" outlined>
          ไม่พบนักศึกษาค้างชำระตามเงื่อนไขที่เลือก
        </v-alert>

        <div v-else class="report-table-wrap">
          <v-simple-table dense class="report-table">
            <thead>
              <tr>
                <th class="text-center">ลำดับ</th>
                <th class="text-center">รหัสนักศึกษา</th>
                <th>ชื่อ-นามสกุล</th>
                <th class="text-right">ยอดค้างชำระรวมค่าปรับ</th>
                <th>ทุน</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="department in groupedItems">
                <tr :key="'dept-' + department.id" class="department-row">
                  <td colspan="5">ภาควิชา{{ department.name }}
                    <span class="float-right">{{ department.count }} คน · {{ formatMoney(department.total) }} บาท</span>
                  </td>
                </tr>
                <template v-for="curriculum in department.curriculums">
                  <tr :key="'curr-' + department.id + '-' + curriculum.id" class="curriculum-row">
                    <td colspan="5">สาขาวิชา{{ curriculum.name }}
                      <span class="float-right">{{ curriculum.count }} คน · {{ formatMoney(curriculum.total) }} บาท</span>
                    </td>
                  </tr>
                  <tr v-for="item in curriculum.items" :key="item.student_id">
                    <td class="text-center">{{ item.running }}</td>
                    <td class="text-center text-no-wrap">
                      <router-link
                        class="student-id-link"
                        :to="{ name: 'payment_officer', query: { student_id: item.student_id } }"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ item.student_id }}
                      </router-link>
                    </td>
                    <td>{{ fullName(item) }}</td>
                    <td class="text-right text-no-wrap font-weight-bold">{{ formatMoney(item.debt_amount) }}</td>
                    <td>{{ item.fund_names || '-' }}</td>
                  </tr>
                </template>
              </template>
            </tbody>
          </v-simple-table>
        </div>
        <div v-if="generatedAt" class="text-caption text-right grey--text mt-2">ออกรายงานเมื่อ {{ generatedAt }}</div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import GenericProvider from "@/services/generic.provider";
import RegistrationProvider from "@/services/registration.provider";
import ReportProvider from "@/services/report.provider";

const genericService = new GenericProvider();
const registrationService = new RegistrationProvider();
const reportService = new ReportProvider();

export default {
  name: "StudentDebtReport",
  data: () => ({
    loading: false,
    formValid: false,
    hasSearched: false,
    years: [],
    semesters: [1, 2, 3],
    faculties: [],
    departments: [],
    curriculums: [],
    items: [],
    paymentConfig: null,
    generatedAt: null,
    summary: { student_count: 0, debt_amount: 0 },
    filters: { levelId: 1, year: null, semester: 1, facultyId: null, departmentId: null, curriculumId: null, fundMode: "all" },
    educationLevels: [
      { value: 1, text: "ระดับปริญญาตรี" },
      { value: 2, text: "ระดับบัณฑิตศึกษา" },
    ],
    fundModes: [
      { value: "all", text: "แสดงทั้งหมด" },
      { value: "nonloan", text: "นักศึกษาที่ไม่กู้" },
      { value: "loan", text: "นักศึกษาที่กู้" },
    ],
    requiredRules: [(value) => !!value || "กรุณาระบุข้อมูล"],
  }),
  computed: {
    filteredDepartments() {
      return this.departments.filter((item) => String(this.departmentFacultyId(item)) === String(this.filters.facultyId));
    },
    filteredCurriculums() {
      return this.curriculums.filter((item) =>
        String(this.curriculumFacultyId(item)) === String(this.filters.facultyId) &&
        String(this.curriculumDepartmentId(item)) === String(this.filters.departmentId));
    },
    groupedItems() {
      const result = [];
      const departments = new Map();
      this.items.forEach((item, index) => {
        if (!departments.has(item.dept_id)) {
          const group = { id: item.dept_id, name: item.dept_name, count: 0, total: 0, curriculums: [], map: new Map() };
          departments.set(item.dept_id, group);
          result.push(group);
        }
        const department = departments.get(item.dept_id);
        if (!department.map.has(item.curr2_id)) {
          const group = { id: item.curr2_id, name: item.curr2_tname, count: 0, total: 0, items: [] };
          department.map.set(item.curr2_id, group);
          department.curriculums.push(group);
        }
        const curriculum = department.map.get(item.curr2_id);
        const debt = Number(item.debt_amount);
        curriculum.items.push({ ...item, running: index + 1 });
        curriculum.count += 1;
        curriculum.total += debt;
        department.count += 1;
        department.total += debt;
      });
      return result;
    },
  },
  methods: {
    facultyId: (item) => item.faculty_id || item.FACULTY_ID,
    facultyName: (item) => item.faculty_name_th || item.FACULTY_NAME_TH || item.faculty_name,
    departmentId: (item) => item.department_id || item.dept_id,
    departmentFacultyId: (item) => item.faculty_id || item.FACULTY_ID,
    departmentName: (item) => item.department_name_th || item.dept_name,
    curriculumId: (item) => item.curriculum_id || item.curr2_id,
    curriculumFacultyId: (item) => item.faculty_id || item.FACULTY_ID,
    curriculumDepartmentId: (item) => item.department_id || item.dept_id,
    curriculumName: (item) => item.curriculum_name_th || item.curr2_tname,
    fullName(item) { return `${item.t_prename || ""}${item.t_name || ""} ${item.t_surname || ""}`.trim(); },
    formatMoney(value) { return Number(value || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 }); },
    formatDate(value) { return value ? `${dayjs(value).format("DD/MM")}/${dayjs(value).year() + 543}` : "-"; },
    onFacultyChanged() { this.filters.departmentId = null; this.filters.curriculumId = null; },
    clearReport() {
      this.items = [];
      this.paymentConfig = null;
      this.generatedAt = null;
      this.summary = { student_count: 0, debt_amount: 0 };
      this.hasSearched = false;
    },
    async onLevelChanged() {
      this.filters.facultyId = null;
      this.filters.departmentId = null;
      this.filters.curriculumId = null;
      this.clearReport();
      this.loading = true;
      try {
        const data = await Promise.all([
          genericService.getRegistrarCurriculum(Number(this.filters.levelId)),
          registrationService.getYearSemesterNow(Number(this.filters.levelId)),
        ]);
        this.curriculums = data[0] || [];
        this.filters.year = Number(data[1].YEAR);
        this.filters.semester = Number(data[1].SEMESTER || 1);
      } catch (error) { this.showError(error); } finally { this.loading = false; }
    },
    showError(error) {
      const data = error.response && error.response.data;
      const message = data ? data.message : error.message;
      this.$toasted.error(typeof message === "string" ? message : JSON.stringify(message), { duration: 6000 });
    },
    async initialize() {
      this.loading = true;
      try {
        const currentYear = dayjs().year() + 543;
        this.years = Array.from({ length: currentYear - 2562 }, (_, index) => currentYear - index);
        const data = await Promise.all([
          genericService.getRegistrarFaculty(),
          genericService.getAllRegistrarDepartment(),
          genericService.getRegistrarCurriculum(1),
          registrationService.getYearSemesterNow(1),
        ]);
        this.faculties = data[0]; this.departments = data[1]; this.curriculums = data[2];
        this.filters.year = Number(data[3].YEAR); this.filters.semester = Number(data[3].SEMESTER || 1);
      } catch (error) { this.showError(error); } finally { this.loading = false; }
    },
    async search() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      try {
        const response = await reportService.getStudentDebtReport({
          level_id: this.filters.levelId, year: this.filters.year, semester: this.filters.semester,
          faculty_id: this.filters.facultyId,
          department_id: this.filters.departmentId || "", curriculum_id: this.filters.curriculumId || "",
          fund_mode: this.filters.fundMode,
        });
        this.items = response.items || []; this.summary = response.summary;
        this.paymentConfig = response.payment_config; this.generatedAt = response.generated_at; this.hasSearched = true;
      } catch (error) { this.showError(error); } finally { this.loading = false; }
    },
    async downloadExcel() {
      if (Number(this.filters.levelId) === 2) return this.downloadGraduateExcel();
      return this.downloadUndergraduateExcel();
    },
    createDebtExcelSheet(columns) {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("รายงาน", {
        pageSetup: {
          paperSize: 9,
          orientation: "portrait",
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 0,
          margins: { left: 0.75, right: 0.25, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
        },
        views: [{ showGridLines: true }],
      });
      sheet.columns = columns;

      const border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
      const font = { name: "Cordia New", size: 14, color: { argb: "FF000000" } };
      const rowsPerPage = 43;

      const styleRow = (row, options = {}) => {
        row.height = 18;
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { ...font, bold: !!options.bold };
          cell.alignment = {
            vertical: "middle",
            horizontal: options.align || "center",
            wrapText: !!options.wrapText,
          };
          if (options.border) cell.border = border;
        });
      };
      const mergeTitle = (text, align = "center", bold = true) => {
        const row = sheet.addRow([text]);
        sheet.mergeCells(row.number, 1, row.number, 6);
        styleRow(row, { align, bold });
      };
      const padToNextPage = () => {
        if (sheet.rowCount === 0 || sheet.rowCount % rowsPerPage === 0) return;
        const numberOfRows = rowsPerPage - (sheet.rowCount % rowsPerPage);
        for (let index = 0; index < numberOfRows; index += 1) {
          const row = sheet.addRow([]);
          row.height = 18;
        }
      };
      return { workbook, sheet, border, styleRow, mergeTitle, padToNextPage };
    },
    async saveDebtExcel(workbook, sheet) {
      sheet.pageSetup.printArea = `A1:F${sheet.rowCount}`;
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `student-debt-${this.filters.year}-${this.filters.semester}.xlsx`);
    },
    async downloadUndergraduateExcel() {
      const columns = [
        { key: "sequence", width: 6 },
        { key: "studentId", width: 12 },
        { key: "studentName", width: 28 },
        { key: "semester", width: 12 },
        { key: "debt", width: 14 },
        { key: "remark", width: 45 },
      ];
      const {
        workbook, sheet, border, styleRow, mergeTitle, padToNextPage,
      } = this.createDebtExcelSheet(columns);
      const reportDate = this.thaiReportDate(this.generatedAt);
      const facultyName = this.selectedFacultyName();
      const detailRowsPerPage = 30;
      let pageNumber = 0;

      const addPageHeader = (departmentName, curriculumName) => {
        pageNumber += 1;
        const pageRow = sheet.addRow(["", "", "", "", "", `หน้า: ${pageNumber}`]);
        styleRow(pageRow, { align: "right" });
        mergeTitle("สถาบันเทคโนโลยีพระจอมเกล้าเจ้าคุณทหารลาดกระบัง");
        mergeTitle("รายงานนักศึกษาค้างชำระ");
        mergeTitle(this.selectedEducationLevelName());
        mergeTitle(`คณะ${facultyName}`);
        mergeTitle(`ภาควิชา${departmentName}`);
        mergeTitle(`สาขาวิชา${curriculumName}`);
        const dateRow = sheet.addRow(["", "", "", "", "", `วันที่ออกรายงาน ${reportDate}`]);
        styleRow(dateRow, { align: "right" });
        const headingRow = sheet.addRow(["ลำดับ", "รหัสนักศึกษา", "ชื่อ-นามสกุล", "ภาคการศึกษา", "ค้างชำระ", "หมายเหตุ"]);
        styleRow(headingRow, { bold: true, border: true, wrapText: true });
      };

      this.groupedItems.forEach((department) => {
        department.curriculums.forEach((curriculum) => {
          let detailNumber = 0;
          curriculum.items.forEach((item) => {
            if (detailNumber % detailRowsPerPage === 0) {
              if (sheet.rowCount > 0) padToNextPage();
              addPageHeader(department.name, curriculum.name);
            }
            detailNumber += 1;
            const remark = item.fund_names
              ? `นศ. ทุน ${item.fund_names} ${this.filters.semester}/${this.filters.year}`
              : "";
            const row = sheet.addRow([
              detailNumber,
              String(item.student_id),
              this.fullName(item),
              `${this.filters.semester}/${this.filters.year}`,
              Number(item.debt_amount),
              remark,
            ]);
            styleRow(row, { border: true });
            row.getCell(2).numFmt = "@";
            row.getCell(3).alignment = { vertical: "middle", horizontal: "left" };
            row.getCell(5).alignment = { vertical: "middle", horizontal: "right" };
            row.getCell(5).numFmt = "#,##0";
            row.getCell(6).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
          });

          const subtotalRow = sheet.addRow(["", "", "", "รวม", curriculum.total, ""]);
          styleRow(subtotalRow, { bold: true });
          subtotalRow.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = { bottom: border.bottom };
          });
          subtotalRow.getCell(5).border = border;
          subtotalRow.getCell(5).numFmt = "#,##0";
          subtotalRow.getCell(5).alignment = { vertical: "middle", horizontal: "right" };
          subtotalRow.getCell(6).border = { bottom: border.bottom, right: border.right };
        });
      });

      const grandTotalRow = sheet.addRow(["", "", "", "รวมทั้งหมด", Number(this.summary.debt_amount), ""]);
      styleRow(grandTotalRow, { bold: true });
      grandTotalRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = { bottom: border.bottom };
      });
      grandTotalRow.getCell(5).border = border;
      grandTotalRow.getCell(5).numFmt = "#,##0";
      grandTotalRow.getCell(5).alignment = { vertical: "middle", horizontal: "right" };
      grandTotalRow.getCell(6).border = { bottom: border.bottom, right: border.right };

      return this.saveDebtExcel(workbook, sheet);
    },
    async downloadGraduateExcel() {
      const columns = [
        { key: "sequence", width: 6 },
        { key: "studentId", width: 12 },
        { key: "studentName", width: 28 },
        { key: "curriculum", width: 38 },
        { key: "debt", width: 30 },
        { key: "remark", width: 45 },
      ];
      const {
        workbook, sheet, border, styleRow, mergeTitle,
      } = this.createDebtExcelSheet(columns);
      const registrationTypeNames = {
        S: "ลงทะเบียนปกติ",
        M: "รักษาสภาพ",
        D: "ลาพัก",
      };
      const registrationTypeOrder = { S: 0, M: 1, D: 2 };
      const reportDate = this.thaiReportDate(this.generatedAt);
      const facultyName = this.selectedFacultyName();
      let pageNumber = 0;

      const addPageHeader = (departmentName) => {
        pageNumber += 1;
        const pageRow = sheet.addRow(["", "", "", "", "", `หน้า: ${pageNumber}`]);
        styleRow(pageRow, { align: "right" });
        mergeTitle("สถาบันเทคโนโลยีพระจอมเกล้าเจ้าคุณทหารลาดกระบัง");
        mergeTitle("รายงานนักศึกษาค้างชำระ");
        mergeTitle(`ระดับบัณฑิตศึกษา / ปีการศึกษา ${this.filters.year} ภาคการศึกษา ${this.filters.semester}`);
        mergeTitle(`คณะ${facultyName}`);
        mergeTitle(`ภาควิชา${departmentName}`);
        const dateRow = sheet.addRow(["", "", "", "", "", `วันที่ออกรายงาน ${reportDate}`]);
        styleRow(dateRow, { align: "right" });
        const headingRow = sheet.addRow([
          "ลำดับ", "รหัสนักศึกษา", "ชื่อ-นามสกุล", "สาขาวิชา", "ค้างชำระ", "หมายเหตุ",
        ]);
        styleRow(headingRow, { bold: true, border: true, wrapText: true });
      };

      this.groupedItems.forEach((department) => {
        const departmentItems = department.curriculums
          .reduce((items, curriculum) => items.concat(curriculum.items), [])
          .sort((left, right) => {
            const leftType = String(left.regis_type || "").toUpperCase();
            const rightType = String(right.regis_type || "").toUpperCase();
            const typeDifference = (registrationTypeOrder[leftType] ?? 99)
              - (registrationTypeOrder[rightType] ?? 99);
            if (typeDifference !== 0) return typeDifference;
            return String(left.student_id).localeCompare(String(right.student_id), "th", { numeric: true });
          });

        if (sheet.rowCount > 0) sheet.addRow([]).height = 18;
        addPageHeader(department.name);
        departmentItems.forEach((item, index) => {
          const registrationType = String(item.regis_type || "").toUpperCase();
          const registrationTypeName = registrationTypeNames[registrationType] || "ค่าธรรมเนียมการศึกษา";
          const degreeName = Number(item.student_level) === 3 ? "ดุษฎีบัณฑิต" : "มหาบัณฑิต";
          const remark = item.fund_names
            ? `นศ. ทุน ${item.fund_names} ${this.filters.semester}/${this.filters.year}`
            : "";
          const row = sheet.addRow([
            index + 1,
            String(item.student_id),
            this.fullName(item),
            `${item.curriculum2_name || "-"} (${degreeName})`,
            `${registrationTypeName} จำนวนเงิน = ${this.formatMoney(item.debt_amount)}`,
            remark,
          ]);
          styleRow(row, { border: true });
          row.getCell(2).numFmt = "@";
          row.getCell(3).alignment = { vertical: "middle", horizontal: "left" };
          row.getCell(4).alignment = { vertical: "middle", horizontal: "left" };
          row.getCell(5).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
          row.getCell(6).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
        });

        const subtotalRow = sheet.addRow(["", "", "", "รวม", department.total, ""]);
        styleRow(subtotalRow, { bold: true });
        subtotalRow.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = { bottom: border.bottom };
        });
        subtotalRow.getCell(5).border = border;
        subtotalRow.getCell(5).numFmt = "#,##0";
        subtotalRow.getCell(5).alignment = { vertical: "middle", horizontal: "right" };
        subtotalRow.getCell(6).border = { bottom: border.bottom, right: border.right };
      });

      const grandTotalRow = sheet.addRow(["", "", "", "รวมทั้งหมด", Number(this.summary.debt_amount), ""]);
      styleRow(grandTotalRow, { bold: true });
      grandTotalRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = { bottom: border.bottom };
      });
      grandTotalRow.getCell(5).border = border;
      grandTotalRow.getCell(5).numFmt = "#,##0";
      grandTotalRow.getCell(5).alignment = { vertical: "middle", horizontal: "right" };
      grandTotalRow.getCell(6).border = { bottom: border.bottom, right: border.right };

      return this.saveDebtExcel(workbook, sheet);
    },
    selectedFacultyName() {
      const faculty = this.faculties.find((item) => String(this.facultyId(item)) === String(this.filters.facultyId));
      return faculty ? this.facultyName(faculty) : "-";
    },
    selectedEducationLevelName() {
      const level = this.educationLevels.find((item) => Number(item.value) === Number(this.filters.levelId));
      return level ? level.text : "-";
    },
    thaiReportDate(value) {
      const date = value ? dayjs(value) : dayjs();
      const monthNames = [
        "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
        "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
      ];
      return `${date.date()} ${monthNames[date.month()]} ${date.year() + 543}`;
    },
  },
  created() { this.initialize(); },
};
</script>

<style scoped>
.summary-card { border-left: 4px solid #e64a19 !important; }
.report-table-wrap { overflow-x: auto; border: 1px solid #e0e0e0; border-radius: 4px; }
.report-table { font-size: 13px; }
.report-table thead th {
  height: 34px !important;
  padding: 0 10px !important;
  background: #455a64;
  color: white !important;
  font-size: 13px;
  white-space: nowrap;
}
.report-table tbody td {
  height: 32px !important;
  padding: 0 10px !important;
}
.student-id-link { font-weight: 600; text-decoration: none; }
.student-id-link:hover { text-decoration: underline; }
.department-row td { background: #ffe0b2; font-weight: 700; }
.curriculum-row td { background: #fff3e0; font-weight: 600; padding-left: 20px !important; }
@media print {
  .report-actions, .v-form { display: none !important; }
  .report-table-wrap { overflow: visible; border: 0; }
}
</style>
